import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

import { Session } from '@/models';

interface SessionContextType {
  sessionsList: Session[];
  setSessionsList: (sessions: Session[]) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  error: string;
  setError: (error: string) => void;
  activeSession: Session | null;
  setActiveSession: (session: Session | null) => void;
  activeSessionId: string | null;
  setActiveSessionId: (id: string | null) => void;
  clearSessionContext: () => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

interface SessionProviderProps {
  children: ReactNode;
}

export const SessionProvider = ({ children }: SessionProviderProps) => {
  // Load stored sessions from sessionStorage
  const storedSessions = sessionStorage.getItem('sessionsList');
  const [sessionsList, setSessionsList] = useState<Session[]>(storedSessions ? JSON.parse(storedSessions) : []);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSession, setActiveSession] = useState<Session | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  // Watch for activeSessionId changes and update activeSession
  useEffect(() => {
    if (activeSessionId && sessionsList.length > 0) {
      const session = sessionsList.find((s) => s.id === activeSessionId);
      setActiveSession(session || null);
    } else {
      setActiveSession(null);
    }
  }, [activeSessionId, sessionsList]);

  // Save sessions to sessionStorage whenever they update
  useEffect(() => {
    if (sessionsList.length > 0) {
      sessionStorage.setItem('sessionsList', JSON.stringify(sessionsList));
    }
  }, [sessionsList]);

  const clearSessionContext = () => {
    setSessionsList([]);
    setIsLoading(true);
    setError('');
    setActiveSession(null);
    setActiveSessionId(null);
    sessionStorage.removeItem('sessionsList'); // Clear storage
  };

  const value = {
    sessionsList,
    setSessionsList,
    isLoading,
    setIsLoading,
    error,
    setError,
    activeSession,
    setActiveSession,
    activeSessionId,
    setActiveSessionId,
    clearSessionContext,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSessionContext = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSessionContext must be used within a SessionProvider');
  }
  return context;
};
